/**
 * Procedural Stack Tower SFX (C017 Phase 4) — the first audio code in this
 * app. Deliberately synthesized via Web Audio oscillators, not sample files:
 * this pass is scoped to a handful of key sounds (hit / perfect / miss /
 * near-miss), not the spec's full 8-category ambient+music system (spec
 * §55-58) — that's a real, separately planned future overhaul (sound
 * matters a lot to the owner; this is the "lock in the mechanics first"
 * placeholder, not the final treatment).
 *
 * One shared `AudioContext`, created lazily on first real use — creating it
 * eagerly at module load would violate browsers' autoplay-gesture policy
 * (a context created before any user gesture starts `suspended` and some
 * browsers never let it resume without one); Stack Tower's own input is
 * always a tap/click, so the very first placement is itself the unlocking
 * gesture. Every sound is a plain oscillator + gain envelope — no samples,
 * no external assets, matching this app's existing zero-image-asset visual
 * style for Stack Tower's own backgrounds.
 */

let ctx: AudioContext | null = null;
let muted = false;

function getContext(): AudioContext | null {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null;
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

export function isStackTowerAudioMuted(): boolean {
  return muted;
}

export function setStackTowerAudioMuted(next: boolean): void {
  muted = next;
}

interface ToneSpec {
  /** Hz. */
  readonly frequency: number;
  /** Seconds. */
  readonly duration: number;
  readonly type?: OscillatorType;
  /** Peak gain, 0-1 — kept low throughout; these play often and must never be startling. */
  readonly peakGain?: number;
}

/** One short tone: a fast attack, exponential decay envelope — reads as a clean "blip", not a buzz or click. */
function playTone({ frequency, duration, type = 'sine', peakGain = 0.15 }: ToneSpec, startOffsetSec = 0): void {
  if (muted) return;
  const audio = getContext();
  if (!audio) return;
  const startAt = audio.currentTime + startOffsetSec;

  const osc = audio.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startAt);

  const gain = audio.createGain();
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  osc.connect(gain);
  gain.connect(audio.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

/** A normal, non-perfect hit — a short, neutral blip. */
export function playStackTowerHit(): void {
  playTone({ frequency: 420, duration: 0.09, type: 'sine', peakGain: 0.12 });
}

/**
 * A perfect placement — brighter than a normal hit, and escalates with the
 * same tier the visual flash does (rules mirrored from stack-tower-render.ts
 * rather than shared code, since one is audio and one is canvas — nothing
 * meaningful to factor out between them beyond the tier *thresholds*, which
 * do live in one place, `multiplierForClimb`'s sibling constants there).
 * Higher tiers layer a second, higher harmonic on top rather than just
 * getting louder — louder-only would get fatiguing fast at a high streak.
 */
export function playStackTowerPerfect(streak: number): void {
  playTone({ frequency: 660, duration: 0.12, type: 'triangle', peakGain: 0.16 });
  if (streak >= 3) playTone({ frequency: 990, duration: 0.14, type: 'triangle', peakGain: 0.1 }, 0.02);
  if (streak >= 10) playTone({ frequency: 1320, duration: 0.18, type: 'sine', peakGain: 0.08 }, 0.05);
}

/** A near-miss (spec §12's "possibly trigger a short tension sound") — sharp and a little dissonant, distinct from both a clean hit and a full miss. */
export function playStackTowerNearMiss(): void {
  playTone({ frequency: 300, duration: 0.1, type: 'sawtooth', peakGain: 0.1 });
}

/** A miss — a low, short descending thud. Two quick tones a fifth apart reads as "falling" without needing a frequency ramp. */
export function playStackTowerMiss(): void {
  playTone({ frequency: 180, duration: 0.16, type: 'square', peakGain: 0.1 });
  playTone({ frequency: 120, duration: 0.2, type: 'square', peakGain: 0.09 }, 0.06);
}
